import { useState, useRef, useEffect } from "react";
import {
	Send,
	Sparkles,
	Bot,
	User,
	RotateCcw,
	Zap,
	MessageSquare,
} from "lucide-react";
import type { ChatMessage } from "@/lib/flow-types";
import { NODE_COLORS } from "@/lib/flow-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

interface ChatPanelProps {
	messages: ChatMessage[];
	onSendMessage: (
		role: ChatMessage["role"],
		content: string,
		nodeId?: string
	) => void;
}

const AI_RESPONSES = [
	"Based on your previous conversations, I can see you're interested in our team plans. Our Pro plan at $79/mo includes everything a growing team needs!",
	"Looking at your chat history, you mentioned having about 20 team members. The Pro plan supports up to 50 seats with full access to all features.",
	"I noticed from our earlier chat that demos were something you were curious about. I can book you a personalized demo this week!",
	"Great question! From what I know about your needs, I'd recommend starting with a 14-day free trial of the Pro plan.",
	"Since you've been exploring our pricing options, let me share a special offer: 20% off your first 3 months when you sign up today!",
];

export function ChatPanel({ messages, onSendMessage }: ChatPanelProps) {
	const [input, setInput] = useState("");
	const [isTyping, setIsTyping] = useState(false);
	const scrollRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
		}
	}, [messages, isTyping]);

	const handleSend = () => {
		if (!input.trim()) return;

		onSendMessage("user", input.trim());
		setInput("");
		setIsTyping(true);

		// Simulate AI response using past chat context
		setTimeout(() => {
			const randomResponse =
				AI_RESPONSES[Math.floor(Math.random() * AI_RESPONSES.length)];
			onSendMessage("bot", randomResponse, "node_ai");
			setIsTyping(false);
		}, 1200 + Math.random() * 800);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	const resetChat = () => {
		onSendMessage(
			"system",
			"Chat session reset"
		);
		onSendMessage(
			"bot",
			"Hey there! Welcome back. How can I help you today?",
			"node_start"
		);
	};

	const formatTime = (date: Date) => {
		return date.toLocaleTimeString([], {
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	return (
		<div className="flex flex-col h-full">
			{/* Header */}
			<div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] bg-gradient-to-r from-indigo-500/10 to-purple-500/10">
				<div className="flex items-center gap-2.5">
					<div className="relative">
						<div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
							<Bot size={18} className="text-white" />
						</div>
						<div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#0d0f14]" />
					</div>
					<div>
						<p className="text-sm font-bold text-zinc-200">
							ChatConvert Bot
						</p>
						<div className="flex items-center gap-1">
							<Zap size={9} className="text-amber-500" />
							<span className="text-[10px] text-zinc-500">
								AI-powered &middot; Context-aware
							</span>
						</div>
					</div>
				</div>
				<Button
					variant="ghost"
					size="sm"
					onClick={resetChat}
					className="text-xs h-7 text-zinc-500 hover:text-zinc-300"
				>
					<RotateCcw size={12} className="mr-1" />
					Reset
				</Button>
			</div>

			{/* Past chats indicator */}
			<div className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/15 flex items-center gap-2">
				<MessageSquare size={12} className="text-amber-500 shrink-0" />
				<p className="text-[10px] text-amber-400">
					<span className="font-semibold">AI Context:</span> Using {messages.filter(m => m.role !== "system").length} past messages to personalize responses
				</p>
			</div>

			{/* Messages */}
			<div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
				{messages
					.filter((m) => m.role !== "system")
					.map((msg) => (
						<div
							key={msg.id}
							className={`flex gap-2.5 ${
								msg.role === "user"
									? "flex-row-reverse"
									: "flex-row"
							}`}
						>
							{/* Avatar */}
							<div
								className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
									msg.role === "user"
										? "bg-zinc-700"
										: "bg-gradient-to-br from-indigo-500 to-purple-600"
								}`}
							>
								{msg.role === "user" ? (
									<User
										size={13}
										className="text-zinc-300"
									/>
								) : (
									<Sparkles
										size={13}
										className="text-white"
									/>
								)}
							</div>

							{/* Message bubble */}
							<div
								className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 ${
									msg.role === "user"
										? "bg-indigo-600 text-white rounded-tr-md"
										: "bg-white/[0.05] text-zinc-200 rounded-tl-md"
								}`}
							>
								<p className="text-[13px] leading-relaxed">
									{msg.content}
								</p>
								<div
									className={`flex items-center gap-1.5 mt-1 ${
										msg.role === "user"
											? "justify-end"
											: "justify-start"
									}`}
								>
									<span
										className={`text-[9px] ${
											msg.role === "user"
												? "text-indigo-300"
												: "text-zinc-500"
										}`}
									>
										{formatTime(msg.timestamp)}
									</span>
									{msg.nodeId && (
										<Badge
											variant="outline"
											className="text-[8px] px-1.5 py-0 h-3.5 border-purple-500/30 text-purple-400"
										>
											<Sparkles
												size={7}
												className="mr-0.5"
											/>
											AI
										</Badge>
									)}
								</div>
							</div>
						</div>
					))}

				{/* Typing indicator */}
				{isTyping && (
					<div className="flex gap-2.5">
						<div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0">
							<Sparkles size={13} className="text-white" />
						</div>
						<div className="bg-white/[0.05] rounded-2xl rounded-tl-md px-4 py-3">
							<div className="flex gap-1">
								<div className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce [animation-delay:0ms]" />
								<div className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce [animation-delay:150ms]" />
								<div className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce [animation-delay:300ms]" />
							</div>
						</div>
					</div>
				)}
			</div>

			{/* Input */}
			<div className="p-3 border-t border-white/[0.06]">
				<div className="flex gap-2">
					<Input
						ref={inputRef}
						value={input}
						onChange={(e) => setInput(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder="Type a message..."
						className="text-sm h-10 rounded-full px-4 bg-white/[0.04] border-white/[0.08] text-zinc-200 placeholder:text-zinc-500"
						disabled={isTyping}
					/>
					<Button
						size="icon"
						onClick={handleSend}
						disabled={!input.trim() || isTyping}
						className="h-10 w-10 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 shrink-0 text-white"
					>
						<Send size={16} />
					</Button>
				</div>
			</div>
		</div>
	);
}
